import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';

import { CHIP_STYLE_TYPES } from '@CommonConstants';

import { ReactComponent as DeleteSmallSVG } from '@Assets/Svgs/ic_delete_small.svg';
import TextCSS from '../Text/Styles';
import TX from '../Text';

const PROBABILITY_LABEL = '확률';

const IFS_LABEL = '보유';

const Chip = ({
  chipRef,
  isBorder,
  type,
  label,
  placeholder,
  value,
  Icon,
  fill,
  onChange,
  onDelete,
  onFocus,
  onBlur,
  onKeyUp,
  probabilityConditionChipVisible,
  calculateProbability,
  resultId,
  results,
}) => {
  const hiddenSpanTextRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const [inputWidth, setInputWidth] = useState(null);

  const isNonEmptyValue = !!value;
  const isProbability = label === PROBABILITY_LABEL;
  const isIfs = label === IFS_LABEL;
  const isShowDeleteButton = type !== CHIP_STYLE_TYPES.TEXT && isFocused;

  const defaultResult = resultId === 0;

  const getLeftChipComponentByType = () => {
    switch (type) {
      case CHIP_STYLE_TYPES.TEXT:
        return (
          <TextWrapper>
            <TX.Caption1>{label}</TX.Caption1>
          </TextWrapper>
        );

      case CHIP_STYLE_TYPES.BG_TEXT:
        return (
          <BackgroundTextWrapper>
            <ProbabilityText>{label}</ProbabilityText>
          </BackgroundTextWrapper>
        );

      case CHIP_STYLE_TYPES.ICON:
        return (
          <IconWrapper>
            <Icon width={16} height={16} fill={fill} />
          </IconWrapper>
        );

      default:
        break;
    }

    return false;
  };

  const getLeftChipComponentByTypesecond = () => {
    switch (type) {
      case CHIP_STYLE_TYPES.TEXT:
        return (
          <TextWrapper>
            <TX.Caption1>{label}</TX.Caption1>
          </TextWrapper>
        );

      case CHIP_STYLE_TYPES.BG_TEXT:
        return (
          <BackgroundTextWrapper>
            <ProbabilityText>{label}</ProbabilityText>
          </BackgroundTextWrapper>
        );

      case CHIP_STYLE_TYPES.ICON:
        return (
          <IconWrapper>
            <Icon width={16} height={16} fill={fill} />
          </IconWrapper>
        );

      default:
        break;
    }

    return false;
  };

  const handleDeleteIconClick = event => {
    if (onDelete) {
      onDelete(event);
    }
  };

  const handleChange = useCallback(
    event => {
      const inputValue = event.target.value;
      // 첫 번째 문자열이 공백이거나 ,뒤에 공백이 오면 함수 종료
      if (inputValue.charAt(0) === ' ' || inputValue.includes(', ')) {
        return;
      }

      if (onChange) {
        onChange(event);
      }

      if (onFocus) {
        onFocus(event);
      }
    },
    [isFocused],
  );

  const handleFocus = useCallback(
    event => {
      if (onFocus) {
        onFocus(event);
      }

      setIsFocused(true);
    },
    [isFocused],
  );

  const handleBlur = useCallback(
    event => {
      if (onBlur) {
        onBlur(event);
      }
      setIsFocused(false);
    },
    [isFocused],
  );

  useEffect(() => {
    setInputWidth(hiddenSpanTextRef.current.offsetWidth);
  }, [value]);

  const haaProperty = results?.some(item => {
    return Object.keys(item.resultPropertyChipState).length > 0;
  });

  const hasRequiredItems = results?.some(item => 'requiredItems' in item.resultPropertyChipState);

  const thisIsDefaltResultAndProb = haaProperty && !hasRequiredItems && defaultResult;

  const thisIsDefaltResultAndRequired = isProbability && probabilityConditionChipVisible && thisIsDefaltResultAndProb;

  return (
    <ChipContainer
      isBorder={isBorder}
      isFocused={isFocused}
      style={{
        backgroundColor: isProbability && probabilityConditionChipVisible ? '#F3F0F0' : null,
        display:
          (!thisIsDefaltResultAndRequired && hasRequiredItems) || (defaultResult && !thisIsDefaltResultAndProb)
            ? 'none'
            : null,
      }}
    >
      {isProbability && probabilityConditionChipVisible ? null : getLeftChipComponentByType()}

      {thisIsDefaltResultAndRequired && getLeftChipComponentByTypesecond()}

      <HiddenSpanText ref={hiddenSpanTextRef}>{value || placeholder}</HiddenSpanText>
      {isProbability && probabilityConditionChipVisible ? null : (
        <StyledChipInput
          ref={chipRef}
          value={value}
          placeholder={placeholder}
          width={inputWidth}
          onInput={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyUp={onKeyUp}
        />
      )}

      {thisIsDefaltResultAndRequired && <DefaultResult>{calculateProbability}%</DefaultResult>}
      {isProbability && !probabilityConditionChipVisible && (
        <PercentageText isNonEmptyValue={isNonEmptyValue}>%</PercentageText>
      )}

      {isShowDeleteButton && !isProbability && !isIfs && (
        <DeleteButton onMouseDown={() => handleDeleteIconClick()}>
          <DeleteSmallSVG />
        </DeleteButton>
      )}
    </ChipContainer>
  );
};

Chip.propTypes = {
  chipRef: PropTypes.shape({
    current: PropTypes.oneOfType([PropTypes.bool, PropTypes.any]),
  }),
  isBorder: PropTypes.bool,
  type: PropTypes.oneOf([CHIP_STYLE_TYPES.TEXT, CHIP_STYLE_TYPES.BG_TEXT, CHIP_STYLE_TYPES.ICON]),
  label: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  Icon: PropTypes.elementType,
  fill: PropTypes.string,
  onChange: PropTypes.func,
  onDelete: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onKeyUp: PropTypes.func,
};

Chip.defaultProps = {
  chipRef: undefined,
  isBorder: false,
  type: CHIP_STYLE_TYPES.ICON,
  label: '',
  placeholder: '',
  Icon: undefined,
  fill: undefined,
  onChange: () => {},
  onDelete: () => {},
  onFocus: () => {},
  onBlur: () => {},
  onKeyUp: () => {},
};

const scrollHiddenCSS = css`
  overflow: -moz-scrollbars-none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    -webkit-appearance: none;
    width: 0;
    height: 0;
    display: none !important;
  }

  & {
    overflow: -moz-scrollbars-none;
  }

  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
`;

const FlexAlignCenterDiv = styled.div`
  display: flex;
  align-items: center;
`;

const ChipContainer = styled(FlexAlignCenterDiv)`
  position: relative;
  padding: ${({ isFocused }) => (isFocused ? '6px' : '6px 10px 6px 6px')};
  border-radius: 8px;

  /* background-color: ${({ isBorder, theme }) => !isBorder && theme.colors.white}; */
  border: ${({ isBorder, isFocused, theme }) =>
    // eslint-disable-next-line no-nested-ternary
    isBorder
      ? isFocused
        ? '2px solid #8FE3EF'
        : `1px solid ${theme.colors.ui_3}`
      : isFocused
      ? '2px solid #8FE3EF'
      : '2px solid transparent'};
`;

const TextWrapper = styled(FlexAlignCenterDiv)`
  justify-content: center;
`;

const BackgroundTextWrapper = styled(FlexAlignCenterDiv)`
  justify-content: center;

  padding: 0px 2px;
  border-radius: 3px;

  /* background-color: ${({ theme }) => theme.colors.ui_3}; */
  background-color: ${({ theme }) => theme.colors.secondary};
`;

const ProbabilityText = styled.span`
  ${({ theme }) => css`
    font-size: 10px;
    line-height: 16px;
    letter-spacing: -0.3px;
    font-weight: ${theme.fontWeights.medium};
    /* color: ${theme.colors.ui_on_3}; */
    color: ${theme.colors.primary};
  `}
`;

const IconWrapper = styled(FlexAlignCenterDiv)`
  justify-content: center;
`;

const HiddenSpanText = styled.span`
  ${scrollHiddenCSS}
  ${TextCSS.FootnoteM}

  visibility: hidden;
  position: absolute;
  top: -10000;
  &:empty:before {
    content: ${({ value }) => (value ? `attr(value)` : `attr(placeholder)`)};
  }
`;

const StyledChipInput = styled.input`
  ${scrollHiddenCSS}
  ${TextCSS.FootnoteM}
  color: ${({ theme }) => theme.colors.ui_on_2};

  display: inline-block;
  width: ${({ width }) => width}px;
  max-width: 400px;
  border: 0;
  padding: 0px;
  margin-left: 4px;
  vertical-align: middle;
  background-color: transparent;
  white-space: nowrap;
  overflow-x: scroll;
  overflow-y: hidden;
  cursor: text;

  &:focus {
    outline: none;
    caret-color: ${({ theme }) => theme.colors.ui_on_2};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.ui_on_4};
  }
`;

const DefaultResult = styled.div`
  ${scrollHiddenCSS}
  ${TextCSS.FootnoteM}
  color: ${({ theme }) => theme.colors.ui_on_2};

  display: inline-block;
  width: ${({ width }) => width}px;
  max-width: 400px;
  border: 0;
  padding: 0px;
  margin-left: 4px;
  vertical-align: middle;
  background-color: transparent;
  white-space: nowrap;
  overflow-x: scroll;
  overflow-y: hidden;
  cursor: text;

  &:focus {
    outline: none;
    caret-color: ${({ theme }) => theme.colors.ui_on_2};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.ui_on_4};
  }
`;

const PercentageText = styled(TX.FootnoteM)`
  margin-left: 5px;

  color: ${({ isNonEmptyValue, theme }) => (isNonEmptyValue ? theme.colors.ui_on_2 : theme.colors.ui_on_4)};
`;

const DeleteButton = styled(FlexAlignCenterDiv)`
  justify-content: center;

  width: 16px;
  height: 16px;
  border-radius: 4px;
  margin-left: 4px;
  cursor: pointer;
  stroke: ${({ theme }) => theme.colors.ui_on_3};

  &:hover {
    background-color: ${({ theme }) => theme.colors.ui_2};
  }
`;

export default Chip;
